<template>
  <div>
    <div class="form-group _margin-bottom-10">
      <div class="col-sm-7">
        <h4 class="question _margin-bottom-30">Please tell us your address</h4>
        <h6 class="_margin-top-20 _margin-bottom-10">Your postcode</h6>
        <validation-messages label="Postcode" :$v="$v.postcode">
          <div class="input-group right">
            <form-field id="postcode" type="nospace" placeholder="Postcode" v-model="quote.postcode"
              :error="$v.postcode.$error" @change="onReset(); $refs.address.onReset()" />

            <div class="input-group-btn">
              <uiv-btn type="primary" :disabled="addressLoader" @click="onGetAddress(quote.postcode)">
                <loader :show="addressLoader" />Search
              </uiv-btn>
            </div>
          </div>
        </validation-messages>
      </div>
    </div>

    <div class="form-group" v-show="addressList.length">
      <div class="col-sm-7">
        <form-label id="searchAddress" v-show="!selectedAddress">Select your address</form-label>

        <validation-messages label="Address" :$v="$v.address">
          <search-select-list ref="address" id="searchAddress" :select-list="addressList" label-key="text"
            :error="$v.address.$error" @onChoose="onChoose" @onReset="onReset">
            {{ selectedAddress }}
          </search-select-list>
        </validation-messages>
      </div>
    </div>

    <div class="_margin-top-20 _margin-bottom-10">
      <small>
        I have read the <a :href="appSettings.supplierTermsAndConditions" target="_blank">Terms & Conditions</a>/<a
          class="padding-left" :href="appSettings.supplierPrivacyPolicy" target="_blank">Privacy Policy</a>
      </small>
    </div>

    <small>
      By continuing, you grant access to your gas or electricity meter information from the national energy industry
      database. This access is necessary to provide you with accurate quotes.
    </small>

    <uiv-alert class="_margin-top-20" type="danger"
      v-if="selectedAddress && !loader && $v.address.domesticMeter == false">
      <b>Oops!</b> <br />
      We are unable to proceed with your request as the meter at the entered address is registered as a
      commercial or non-domestic energy meter. We cannot process non domestic metered properties.

      <div class="row _margin-top-20" v-if="!appSettings.whiteLabel">
        <div class="col-xs-6 col-md-4">
          <small><b>Call Us</b></small><br />
          {{ appSettings.nonDomesticMeterPhone }}
        </div>

        <div class="col-xs-6 col-md-8">
          <small><b>Opening hours</b></small><br />
          {{ appSettings.nonDomesticMeterPhoneOpenHours }}
        </div>
      </div>
    </uiv-alert>

    <uiv-alert class="_margin-top-20" type="danger"
      v-else-if="selectedAddress && !loader && $v.address.prePaymentMeter == false">
      <b>Oops!</b> <br />
      We are unable to proceed with your request as the meter at the entered address is identified as a Pre Payment
      meter. We cannot process Pre Payment metered properties.
    </uiv-alert>

    <uiv-alert class="_margin-top-20" type="danger"
      v-else-if="selectedAddress && !loader && $v.address.gasOnly == false">
      <b>Oops!</b> <br />
      We are unable to proceed with your request as the meter at the entered address is identified as a single gas
      meter. We cannot process single gas metered properties.
    </uiv-alert>

    <uiv-alert class="_margin-top-20" type="danger"
      v-else-if="selectedAddress && !loader && $v.address.noElectricMeterData == false">
      <b>Oops!</b> <br />
      We are unable to find national data for this address.
    </uiv-alert>

    <div class="card-footer">
      <div class="col-xs-6">
        <uiv-btn @click="onBack">Back</uiv-btn>
      </div>
      <div class="col-xs-6 text-right">
        <uiv-btn type="primary" @click="onGoToQuestion('next')"
          :disabled="this.$v.postcode.$invalid || this.$v.address.$invalid || loader">
          <loader :show="loader" />
          Next
        </uiv-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'YourHome',
  data() {
    return {
      loader: false,
      selectedAddress: null,
      noMpanOrMprn: false
    };
  },
  props: {
    $v: Object,
    skipTerms: Boolean
  },
  computed: {
    ...mapState({
      appSettings: ({ app_settings }) => app_settings,
      quote: ({ quote }) => quote,
      addressList: ({ lookups }) => lookups.addresses,
      addressLoader: ({ lookups }) => lookups.requesting.addresses,
      xoserveMetersLoader: ({ lookups }) => lookups.requesting.xoserveMeters
    })
  },
  methods: {
    ...mapActions({
      getAddresses: 'lookups/getAddresses',
      getXoserveMeters: 'lookups/getXoserveMeters',
      resetList: 'lookups/resetList',
      getSuppliers: 'lookups/getSuppliers',
      getDefaults: 'quote/getDefaults',
      goToQuestion: 'quote_questions/goToQuestion'
    }),
    onReset() {
      this.quote.address = {};
      this.selectedAddress = null;
      this.quote.postcode = this.quote.postcode.toUpperCase();
    },
    onConfirmGdpr() {
      this.quote.gdprConsent = true;
      this.getAddresses(this.quote.postcode);
    },
    onGetAddress(postcode) {
      this.$refs.address.onReset();
      this.$v.postcode.$touch();

      if (this.$v.postcode.$invalid) return;

      this.$v.$reset();

      this.getAddresses(postcode);
    },
    async onChoose(selected) {
      this.selectedAddress = selected.text;
      this.loader = true;
      const xoserveMeters = await this.getXoserveMeters(selected);

      const { mpan, mprn } = xoserveMeters;

      this.quote.address = {
        parts: { ...selected, ...xoserveMeters },
        text: selected.text
      };

      const data = await this.getDefaults(xoserveMeters);
      this.setDefaults(data);
      this.loader = false;
      this.noMpanOrMprn = !mpan || !mprn;
      this.$v.address.$reset();

      this.$store.dispatch('tracing/addAction', { name: 'Step', value: 'Your Home' });
      this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Choose' });
      this.$store.dispatch('tracing/addAction', { name: 'Value', value: selected.text });
      this.$store.dispatch('tracing/saveActions');
    },
    setDefaults(data) {
      let { gasdetails, elecdetails } = this.quote.address.parts;

      gasdetails = gasdetails.find(({ isDomestic }) => isDomestic) || {};
      elecdetails = elecdetails.find(({ isDomestic }) => isDomestic) || {};

      const twoSuppliers = data.gas?.supplierId != null && data.electricity?.supplierId != null;
      this.quote.supplierId = data.gas?.supplierId || data.electricity?.supplierId;
      this.quote.gasUsage.usage = data.gas?.consumption;
      this.quote.gasUsage.usageType = 1;
      this.quote.gasUsage.usageUnit = 1;
      this.quote.electricityUsage.usageType = 1;
      this.quote.electricityUsage.usageUnit = 1;
      this.quote.currentPayMethod = 'monthlyDirectDebit';
      this.quote.electricityCurrentPayMethod = 'monthlyDirectDebit';
      this.quote.gasCurrentPayMethod = 'monthlyDirectDebit';
      this.quote.gasSupplierId = data.gas?.supplierId;
      this.quote.electricitySupplierId = data.electricity?.supplierId;
      this.quote.electricitySmartMeterInstalled = elecdetails.isSmartMeter;
      this.quote.gasSmartMeterInstalled = gasdetails.isSmartMeter;
      this.quote.newTariffPaymentMethod = 'monthlyDirectDebit';
      this.quote.sameSupplier = data.gas?.supplierId == data.electricity?.supplierId ? 'yes' : 'no';
      this.quote.electricityUsage.disabledUnits = false;

      this.quote.propertyId = null;
      this.quote.solarPropertyId = data.solarPropertyId;
      this.quote.ashpPropertyId = data.ashpPropertyId;
      this.quote.vehicleUsage.weekday = 0;
      this.quote.vehicleUsage.weekend = 0;

      this.quote.gasUsage.usage = data.gas?.consumption;
      this.quote.gasUsage.cost = null;
      this.quote.electricityUsage.usage = data.electricity?.consumption;
      this.quote.gasUsage.cost = null;
      this.quote.electricityTariff.id = data.electricity?.tariffId;
      this.quote.gasTariff.id = data.gas?.tariffId;

      this.quote.heatingFuel = 1;

      if (twoSuppliers) {
        this.quote.compareFuel = 'gasElectricity';
        this.quote.address.parts.fuelType = 'gasElectricity';
        this.quote.heatingFuel = 1;
      } else {
        this.quote.compareFuel = gasdetails.mugSupplierId ? 'gas' : 'electricity';
        this.quote.address.parts.fuelType = gasdetails.mugSupplierId ? 'gas' : 'electricity';
      }

      if (this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'yes') {
        this.quote.tariff.id = data.electricity?.tariffId || data.gas?.tariffId;
        if (elecdetails.isPrepayment == true && gasdetails.isPrepayment == true) {
          this.quote.currentPayMethod = 'prePaymentMeter';
        }
      }

      if ((this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'no') || this.quote.compareFuel == 'gas') {
        if (gasdetails.isPrepayment == true) {
          this.quote.gasCurrentPayMethod = 'prePaymentMeter';
        }

        this.quote.electricityUsage.disabledUnits = true;
      }

      if ((this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'no') || this.quote.compareFuel == 'electricity') {
        if (elecdetails.isPrepayment == true) {
          this.quote.electricityCurrentPayMethod = 'prePaymentMeter';
        }
      }

      if (this.quote.compareFuel == 'electricity') {
        this.quote.heatingFuel = 2;

        return this.getSuppliers('electricity');
      }

      if (this.quote.compareFuel == 'gas') {
        return this.getSuppliers('gas');
      }

      if (this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'yes') {
        this.quote.electricityUsage.disabledUnits = true;
        this.getSuppliers(this.quote.compareFuel);
      } else {
        this.getSuppliers('gas');
        this.getSuppliers('electricity');
      }
    },
    async onBack() {
      this.$store.dispatch('tracing/addAction', { name: 'Step', value: 'Your Home' });
      this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Back' });
      await this.$store.dispatch('tracing/saveActions');
      this.$router.push('/home');
    },
    async onGoToQuestion(type) {
      this.$v.postcode.$touch();
      this.$v.address.$touch();

      if (this.$v.postcode.$invalid || this.$v.address.$invalid) return;

      this.$store.dispatch('tracing/addAction', { name: 'Step', value: 'Your Home' });
      this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Next' });

      await this.goToQuestion({ type });
      await this.$store.dispatch('tracing/saveActions');
    }
  }
};
</script>

<style lang="scss"></style>
